// flowCompletionConfig.ts
import type { UserSession } from '../hooks/useSessionPersistence';
import type { FlowId } from './frameFlowMap';

type CompletionConfig = {
  completeTaskId: string; // what gets pushed into completedTasks
  taskIdsToCheck: string[]; // subtasks to show/check for emptiness
  hasMeaningfulData: (session: UserSession) => boolean;
  emptyModal: { title: string; body: string; confirmText: string; cancelText: string };
  taskLabels: Record<string, string>;
};

const hasNonEmptyArray = (v: unknown) => Array.isArray(v) && v.length > 0;
const hasNonEmptyString = (v: unknown) => typeof v === 'string' && v.trim().length > 0;

/**
 * Deep “meaningful” check:
 * - "" / whitespace -> not meaningful
 * - [] -> not meaningful (unless it contains meaningful elements)
 * - {} -> not meaningful (unless it contains meaningful values)
 * - boolean -> meaningful only if true (prevents default false flags counting as “data”)
 * - number -> meaningful if present (0 counts as a value)
 */
const hasMeaningfulValue = (v: unknown): boolean => {
  if (v == null) return false;

  if (typeof v === 'string') return v.trim().length > 0;

  if (typeof v === 'number') return true;

  if (typeof v === 'boolean') return v === true;

  if (Array.isArray(v)) return v.some(hasMeaningfulValue);

  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).some(hasMeaningfulValue);

  return false;
};

export const FLOW_COMPLETION_CONFIG: Partial<Record<FlowId, CompletionConfig>> = {
  about: {
    completeTaskId: 'about',
    taskIdsToCheck: ['about', 'passwords', 'contacts'],
    hasMeaningfulData: (session) => {
      const aboutAnswers = session.taskProgress?.about?.answers;
      const hasAbout = !!aboutAnswers && hasMeaningfulValue(aboutAnswers);

      const pwList = session.taskProgress?.passwords?.answers?.passwords;
      const hasPasswords = Array.isArray(pwList) && pwList.length > 0;

      const contactsAnswers = session.taskProgress?.contacts?.answers;
      const contactsCount = (contactsAnswers as { contactsCount?: number })?.contactsCount;

      const hasContactsCount = typeof contactsCount === 'number' && contactsCount > 0;

      return hasAbout || hasPasswords || hasContactsCount; //
    },
    emptyModal: {
      title: 'Nothing added in Setup Your Plan',
      body: 'You haven’t added any details yet. If this section doesn’t apply, you can still finish it.',
      confirmText: 'Yes, skip it',
      cancelText: 'Go back',
    },
    taskLabels: {
      about: 'Setup details',
      passwords: 'Password access',
      contacts: 'Contacts'
    },
  },

  legacy: {
    completeTaskId: 'legacy',
    taskIdsToCheck: ['memoryCollections', 'legacy'],
    hasMeaningfulData: (session) => {
      const mc = session.taskProgress?.memoryCollections?.answers;
      const lg = session.taskProgress?.legacy?.answers;

      // MemoryCollections: DO NOT count “person added” as meaningful.
      // We treat only actual content (strings/files/notes/etc) or explicit collections/items as meaningful.
      const mcMeaningful =
        !!mc &&
        (hasNonEmptyArray((mc as Record<string, unknown>).collections) ||
          hasNonEmptyArray((mc as Record<string, unknown>).items) ||
          Object.entries(mc).some(([k, v]) => {
            const ignore = new Set(['collections', 'items', 'people', 'persons', 'contacts']);
            if (ignore.has(k)) return false;
            return hasMeaningfulValue(v);
          }));

      // Legacy: unknown meaningful text/value counts (not just object keys existing)
      const lgMeaningful = !!lg && hasMeaningfulValue(lg);

      return mcMeaningful || lgMeaningful;
    },
    emptyModal: {
      title: 'Nothing added in Memories and Messages',
      body: 'You haven’t added any memories or messages. If you don’t want to add anything right now, you can still finish this section.',
      confirmText: 'Yes, skip it',
      cancelText: 'Go back',
    },
    taskLabels: {
      memoryCollections: 'Memories / collections',
      legacy: 'Legacy notes',
    },
  },

  inventory: {
    completeTaskId: 'inventory',
    taskIdsToCheck: ['financial_assets', 'properties', 'tax_documents', 'inventory'],
    hasMeaningfulData: (session) => {
      const fa = session.taskProgress?.financial_assets?.answers;
      const cash = fa?.cashAccounts;
      const invAcct = fa?.investmentAccounts;
      const ret = fa?.retirementAccounts;
      const other = fa?.otherFinancialAssets;
      const income = fa?.incomeSources;

      const props = session.taskProgress?.properties?.answers?.properties;
      const taxDocs = session.taskProgress?.tax_documents?.answers?.documents;

      const inv = session.taskProgress?.inventory?.answers;

      if (hasNonEmptyArray(cash)) return true;
      if (hasNonEmptyArray(invAcct)) return true;
      if (hasNonEmptyArray(ret)) return true;
      if (hasNonEmptyArray(other)) return true;
      if (hasNonEmptyArray(income)) return true;

      if (hasNonEmptyArray(props)) return true;
      if (hasNonEmptyArray(taxDocs)) return true;

      // Optional: unknown “inventory” freeform answers
      return !!inv && hasMeaningfulValue(inv);
    },
    emptyModal: {
      title: 'Nothing added in Inventory of Assets',
      body: 'You haven’t added any assets yet. If you have no assets to list, you can still finish this section.',
      confirmText: 'Yes, none apply',
      cancelText: 'Go back',
    },
    taskLabels: {
      financial_assets: 'Financial accounts',
      properties: 'Properties / assets',
      tax_documents: 'Tax returns',
      inventory: 'Inventory details',
    },
  },

  funeral: {
    completeTaskId: 'funeral',
    taskIdsToCheck: ['funeral'],
    hasMeaningfulData: (session) => {
      const f = session.taskProgress?.funeral?.answers;
      if (!f) return false;

      const fp = (f as Record<string, unknown>).funeralPreferences;
      const md = (f as Record<string, unknown>).memorialDetails;
      const fi = (f as Record<string, unknown>).finalInstructions;

      // IMPORTANT: do NOT use “has keys” here; these objects always have keys even when empty.
      return hasMeaningfulValue(fp) || hasMeaningfulValue(md) || hasMeaningfulValue(fi);
    },
    emptyModal: {
      title: 'Nothing added in Funeral & Memorial',
      body: 'You haven’t added any funeral or memorial preferences. If you don’t want to specify anything, you can still finish this section.',
      confirmText: 'Yes, skip it',
      cancelText: 'Go back',
    },
    taskLabels: { funeral: 'Funeral preferences' },
  },

  personal: {
    completeTaskId: 'personal',
    taskIdsToCheck: ['personal'],
    hasMeaningfulData: (session) => {
      const p = session.taskProgress?.personal?.answers;
      if (!p) return false;

      // Most commonly: { personalItems: [] } — that should be EMPTY.
      // Count as meaningful only if any value is meaningful (e.g., array has items, notes filled, etc).
      return hasMeaningfulValue(p);
    },
    emptyModal: {
      title: 'Nothing added in Personal Accounts and Belongings',
      body: 'You haven’t added any personal account or belonging info. If none apply, you can still finish this section.',
      confirmText: 'Yes, none apply',
      cancelText: 'Go back',
    },
    taskLabels: { personal: 'Personal accounts / belongings' },
  },

  debts: {
    completeTaskId: 'debts',
    taskIdsToCheck: ['debts', 'monthly_expenses'],
    hasMeaningfulData: (session) => {
      const d = session.taskProgress?.debts?.answers;
      const me = session.taskProgress?.monthly_expenses?.answers;

      const debtsArr = (d as Record<string, unknown> | undefined)?.debts;
      const expArr = (me as Record<string, unknown> | undefined)?.expenses;

      return (
        hasNonEmptyArray(debtsArr) ||
        hasNonEmptyArray(expArr) ||
        (!!d && hasMeaningfulValue(d)) ||
        (!!me && hasMeaningfulValue(me))
      );
    },
    emptyModal: {
      title: 'Nothing added in Debts and Final Expenses',
      body: 'You haven’t added any debts or final expenses. If you have none, you can still finish this section.',
      confirmText: 'Yes, none apply',
      cancelText: 'Go back',
    },
    taskLabels: {
      debts: 'Debts / liabilities',
      monthly_expenses: 'Monthly expenses',
    },
  },

  distribution: {
    completeTaskId: 'distribution',
    taskIdsToCheck: ['assetDistribution'],
    hasMeaningfulData: (session) => {
      const d = session.taskProgress?.assetDistribution?.answers;
      if (!d) return false;

      const dist = (d as Record<string, unknown>).distributions;

      if (Array.isArray(dist)) return dist.length > 0;
      if (dist && typeof dist === 'object') return Object.keys(dist).length > 0;

      return hasMeaningfulValue(d);
    },
    emptyModal: {
      title: 'Nothing added in Asset Distribution',
      body: 'You haven’t added any distribution details. If you’re not ready to specify this, you can still finish this section.',
      confirmText: 'Yes, skip it',
      cancelText: 'Go back',
    },
    taskLabels: { assetDistribution: 'Distribution plan' },
  },

  pets: {
    completeTaskId: 'pets',
    taskIdsToCheck: ['pets'],
    hasMeaningfulData: (session) => {
      const p = session.taskProgress?.pets?.answers;
      if (!p) return false;

      const pad = (p as Record<string, unknown>).petsAndDependants as
        | Record<string, unknown>
        | undefined;
      const petsArr = pad?.pets ?? (p as Record<string, unknown>).pets;
      const depsArr =
        pad?.dependants ?? (p as Record<string, unknown>).dependants ?? (p as Record<string, unknown>).dependents;

      const hasPetsOrDeps = hasNonEmptyArray(petsArr) || hasNonEmptyArray(depsArr);

      const hasOther =
        hasMeaningfulValue((p as Record<string, unknown>).financialArrangements) ||
        hasMeaningfulValue((p as Record<string, unknown>).finalInstructions);

      return hasPetsOrDeps || hasOther;
    },
    emptyModal: {
      title: 'Nothing added in Pets and Dependents',
      body: 'You haven’t added any pets or dependents. If none apply, you can still finish this section.',
      confirmText: 'Yes, none apply',
      cancelText: 'Go back',
    },
    taskLabels: { pets: 'Pets / dependents' },
  },

  legal_essentials: {
    completeTaskId: 'legal_essentials',
    taskIdsToCheck: ['legal_documents', 'legal_essentials'],
    hasMeaningfulData: (session) => {
      const docs = session.taskProgress?.legal_documents?.answers?.documents;

      // where Frame261 actually saves
      const execs = session.taskProgress?.legal_essentials?.answers?.executors;
      const note = session.taskProgress?.legal_essentials?.answers?.executorNote;

      return hasNonEmptyArray(docs) || hasNonEmptyArray(execs) || hasNonEmptyString(note);
    },
    emptyModal: {
      title: 'Nothing added in Legal Essentials',
      body: 'You haven’t uploaded any documents or selected an executor. If none apply, you can still finish this section.',
      confirmText: 'Yes, none apply',
      cancelText: 'Go back',
    },
    taskLabels: {
      legal_documents: 'Legal documents',
      legal_essentials: 'Executor',
    },
  },
};